import { useReadQuery } from "@apollo/client";
import { Box, Tooltip, Typography } from "@mui/material";
import { grey } from "@mui/material/colors";
import { TruncateTypography } from "components/TruncateTypography";
import { useMemo } from "react";

import { useWasteOverviewState } from "../../WasteOverviewContext";
import {
  classificationColors,
  classificationLabels,
  classificationTooltips,
  wasteCategories,
} from "./constants";
import { normalizeData } from "./normalizeArrayData";

const MINIMUM_PERCENTAGE = 3;

export function TotalWasteChart() {
  const { totalWasteQuery } = useWasteOverviewState();
  const { data } = useReadQuery(totalWasteQuery);

  const { periodPercentChange, previousPeriodTotal, wasteByClassification } = data.wasteTotalWaste;

  const totalWaste = useMemo(
    () => wasteByClassification.reduce((sum, item) => sum + item.totalAmountInPounds, 0),
    [wasteByClassification]
  );

  const normalizedWaste = useMemo(() => {
    return normalizeData(wasteByClassification, {
      minimumPercentage: MINIMUM_PERCENTAGE,
    });
  }, [wasteByClassification]);
  // Helper function to get data for a specific category
  const getCategoryData = useMemo(
    () => (classification: string) => {
      const categoryData = normalizedWaste.find((item) => item.classification === classification);
      return {
        classification,
        totalAmountInPounds: categoryData?.totalAmountInPounds ?? 0,
        percentage: categoryData?.percentage ?? 0,
        normalizedPercentage: categoryData?.normalizedPercentage ?? 0,
      };
    },
    [normalizedWaste]
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", lg: "row" },
        gap: 2,
      }}
    >
      {/* Column 1: Total Waste */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
          minWidth: { xs: "auto", lg: 140 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "baseline",
            gap: 0.25,
          }}
        >
          <Typography
            sx={{
              fontSize: "2rem",
              fontWeight: 400,
              color: "text.secondary",
            }}
          >
            {Math.round(totalWaste).toLocaleString()}
          </Typography>
          <Typography
            sx={{
              fontSize: "0.75rem",
              fontWeight: 400,
              color: "text.secondary",
            }}
          >
            lbs
          </Typography>
        </Box>
        <PeriodPercentChangeDisplay
          periodPercentChange={periodPercentChange}
          previousPeriodTotal={previousPeriodTotal}
        />
        {totalWaste === 0 && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              mt: 1,
            }}
          >
            <Typography
              sx={{
                fontSize: "0.875rem",
                color: "text.secondary",
              }}
            >
              🪫 No waste to show
            </Typography>
          </Box>
        )}
      </Box>

      {/* Column 2: Chart and Breakdown */}
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 2 }}>
        {/* Row 1: Horizontal Bar Chart */}
        <Box
          sx={{
            display: "flex",
            height: 32,
            borderRadius: "4px",
            overflow: "hidden",
          }}
        >
          {wasteCategories.map((classification) => {
            const categoryData = getCategoryData(classification);
            // When total waste is 0, show equal width for all categories
            const displayWidth =
              totalWaste === 0
                ? `${100 / wasteCategories.length}%`
                : `${categoryData.normalizedPercentage}%`;

            const lastItem = classification === wasteCategories[wasteCategories.length - 1];
            return (
              <Box
                key={classification}
                sx={{
                  backgroundColor:
                    totalWaste === 0
                      ? grey[100] // Gray color when no data
                      : classificationColors[classification] || "#ccc",
                  width: displayWidth,
                  borderRight: totalWaste === 0 || lastItem ? "0" : "2px solid white",
                }}
              />
            );
          })}
        </Box>

        {/* Row 2: Four Column Breakdown */}
        <Box
          sx={{
            display: "flex",
            flexDirection: ["column", "row"],
            flexWrap: ["nowrap", "wrap", "wrap", "wrap", "nowrap"],
            gap: 2,
          }}
        >
          {wasteCategories.map((classification) => {
            const categoryData = getCategoryData(classification);

            const displayPercentage =
              categoryData.percentage < 1 ? "<1" : Math.round(categoryData.percentage);
            return (
              <Box
                key={classification}
                sx={{
                  flex: [
                    "none",
                    "0 0 calc(50% - 0.5rem)",
                    "0 0 calc(50% - 0.5rem)",
                    "0 0 calc(50% - 0.5rem)",
                    1,
                  ],
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  padding: "0.5rem 1rem",
                  backgroundColor: "#F5F5F5",
                  borderRadius: "0.25rem",
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    backgroundColor: classificationColors[classification] || "#ccc",
                    borderRadius: "2px",
                    flexShrink: 0,
                  }}
                />
                <Box sx={{ flex: 1 }}>
                  <Tooltip
                    title={classificationTooltips[classification] || classification}
                    placement="top"
                    arrow
                  >
                    <TruncateTypography
                      sx={{
                        fontSize: "0.875rem",
                        fontWeight: 400,
                        color: "text.secondary",
                        mb: 0.5,
                        cursor: "help",
                      }}
                    >
                      {classificationLabels[classification] || classification}
                    </TruncateTypography>
                  </Tooltip>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "baseline",
                      gap: 0.25,
                      mb: 0.25,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "1.5rem",
                        fontWeight: 400,
                      }}
                    >
                      {Math.round(categoryData.totalAmountInPounds).toLocaleString()}
                    </Typography>
                    <TruncateTypography
                      sx={{
                        fontSize: "0.75rem",
                        fontWeight: 500,
                      }}
                    >
                      lbs {categoryData.totalAmountInPounds !== 0 ? `| ${displayPercentage}%` : ""}
                    </TruncateTypography>
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}

interface PeriodPercentChangeDisplayProps {
  periodPercentChange: number | null | undefined;
  previousPeriodTotal: number | null | undefined;
}

function PeriodPercentChangeDisplay({
  periodPercentChange,
  previousPeriodTotal,
}: PeriodPercentChangeDisplayProps) {
  if (periodPercentChange === null || periodPercentChange === undefined) {
    return null;
  }

  let changeText: string;
  let textColor: string;

  if (periodPercentChange > 0) {
    changeText = `${Math.round(periodPercentChange)}% higher`;
    textColor = "error.main";
  } else if (periodPercentChange < 0) {
    changeText = `${Math.round(Math.abs(periodPercentChange))}% lower`;
    textColor = "success.main";
  } else {
    changeText = "No change";
    textColor = "text.secondary";
  }

  return (
    <Box>
      <Typography
        sx={{
          fontSize: "0.875rem",
          fontWeight: 400,
          color: textColor,
        }}
      >
        {changeText} than the same period last year
      </Typography>
      <Typography
        sx={{
          fontSize: "0.875rem",
          fontWeight: 400,
          color: "text.secondary",
        }}
      >
        Previous period total: {Math.round(previousPeriodTotal ?? 0).toLocaleString()} lbs
      </Typography>
    </Box>
  );
}
